import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PawPrint, ShieldCheck, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { hapticTap } from "@/lib/haptics";

function ageLabel(birth) {
  if (!birth) return null;
  const months = Math.floor((Date.now() - new Date(birth)) / (30.44 * 86400000));
  if (months < 0 || Number.isNaN(months)) return null;
  return months < 12 ? `${months} mesi` : `${Math.floor(months / 12)} ${months < 24 ? "anno" : "anni"}`;
}

const SPRING = { type: "spring", stiffness: 350, damping: 25 };

export default function DogHero({ dogs, dogId, onChange, docsCount, expiringCount }) {
  const idx = Math.max(0, dogs.findIndex((d) => d.id === dogId));
  const dog = dogs[idx];
  const go = (dir) => {
    const n = dogs[(idx + dir + dogs.length) % dogs.length];
    if (n && n.id !== dogId) { hapticTap(); onChange(n.id); }
  };
  if (!dog) return null;
  const age = ageLabel(dog.birth_date);

  return (
    <div data-testid="drawer-dog-hero">
      <motion.div
        drag={dogs.length > 1 ? "x" : false}
        dragConstraints={{ left: 0, right: 0 }} dragElastic={0.25}
        onDragEnd={(_, i) => { if (i.offset.x < -60) go(1); else if (i.offset.x > 60) go(-1); }}
        className="relative h-[340px] sm:h-[420px] lg:h-[460px] rounded-[32px] overflow-hidden select-none touch-pan-y"
        style={{ boxShadow: "0 30px 60px -30px rgba(120,60,10,0.45)" }}>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div key={dog.id} className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
            {dog.photo_url ? (
              <img src={dog.photo_url} alt={dog.name} draggable={false} className="h-full w-full object-cover" style={{ objectPosition: "center 30%" }} />
            ) : (
              <div className="h-full w-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg,#FFE8CB 0%,#F4B83C 55%,#FF7A1A 100%)" }}>
                <PawPrint className="h-24 w-24 text-white/70" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 45%, rgba(20,10,4,0.78) 100%)" }} />

        {dogs.length > 1 && (
          <>
            <div className="absolute top-4 inset-x-0 flex justify-center gap-1.5">
              {dogs.map((d) => (
                <motion.span key={d.id} layout transition={SPRING}
                  className="h-1.5 rounded-full bg-white" style={{ width: d.id === dogId ? 22 : 6, opacity: d.id === dogId ? 1 : 0.55 }} />
              ))}
            </div>
            <button onClick={() => go(-1)} aria-label="Cane precedente" data-testid="dog-hero-prev"
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur items-center justify-center">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => go(1)} aria-label="Cane successivo" data-testid="dog-hero-next"
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur items-center justify-center">
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
          <AnimatePresence mode="wait">
            <motion.div key={dog.id}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}>
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/75 font-semibold">Il cassetto di</div>
              <h1 data-testid="drawer-dog-title" className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-1 truncate">
                {dog.name}
              </h1>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[dog.breed, age].filter(Boolean).map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-white/20 backdrop-blur-md border border-white/25">{t}</span>
                ))}
                <span className="px-2.5 py-1 rounded-full text-[11.5px] font-medium bg-white/20 backdrop-blur-md border border-white/25 inline-flex items-center gap-1">
                  <FileText className="h-3 w-3" /> {docsCount} documenti
                </span>
                {expiringCount > 0 ? (
                  <span className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-[#E94545] inline-flex items-center gap-1">
                    {expiringCount} in scadenza
                  </span>
                ) : dog.microchip ? (
                  <span className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-[#2DBA60]/90 inline-flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Microchip
                  </span>
                ) : null}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {dogs.length > 1 && (
        <div className="mt-3 flex gap-2.5 overflow-x-auto no-scrollbar pb-1" data-testid="dog-switcher">
          {dogs.map((d) => {
            const active = d.id === dogId;
            return (
              <motion.button key={d.id} whileTap={{ scale: 0.92 }} onClick={() => { hapticTap(); onChange(d.id); }}
                data-testid={`dog-switcher-avatar-${d.id}`}
                className="relative shrink-0 flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full border bg-white"
                style={{ borderColor: active ? "#FF7A1A" : "#F0EBE3" }}>
                {active && <motion.span layoutId="dog-switch-pill" transition={SPRING} className="absolute inset-0 rounded-full bg-[#FFF3E6]" />}
                <span className="relative h-9 w-9 rounded-full overflow-hidden bg-[#F4EFE6] flex items-center justify-center">
                  {d.photo_url ? <img src={d.photo_url} alt="" className="h-full w-full object-cover" /> : <PawPrint className="h-4 w-4 text-[#C25612]" />}
                </span>
                <span className={`relative text-[13px] ${active ? "font-semibold text-[#1A1A1A]" : "text-[#6B6B6B]"}`}>{d.name}</span>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
